import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { useCheckin, useResetClock, rewardInfo, buttonState, clockText, eventImg, turnPct, whenText } from "../checkinLogic";
import { Empty, LoginGate, Skeleton } from "../ui";

/*
 * หน้า "เช็คอินรายวัน" — ดีไซน์ landing-ver2 (เส้นทางรางวัลนีออน): รางเส้นเรืองแสงเดินจากวันแรกไปถึงหีบรางวัลวันสุดท้าย
 * (ตรรกะอยู่ที่ checkinLogic.js ร่วมกับ landing ซึ่งใช้ดีไซน์สมุดแสตมป์แทน)
 */

function Reward({ result, onClose }) {
  const closeRef = useRef(null);
  useEffect(() => {
    if (!result) return undefined;
    if (closeRef.current) closeRef.current.focus();
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [result, onClose]);
  if (!result) return null;
  const ok = result.ok;
  const r = ok ? result.reward : null;
  const cash = r && r.rewardType === "cashWallet";
  return (
    <div className="ckn-overlay" role="dialog" aria-modal="true" aria-label={ok ? "เช็คอินสำเร็จ" : "เช็คอินไม่สำเร็จ"} onClick={onClose}>
      <div className="ckn-pop" onClick={(e) => e.stopPropagation()}>
        {ok ? (
          <>
            <div className="ckn-pop-rays" aria-hidden="true" />
            <Icon icon={cash ? "fluent-emoji-flat:money-bag" : "fluent-emoji-flat:gem-stone"} width={92} height={92} className="ckn-pop-ic" />
            <div className="ckn-pop-kicker">
              <Icon icon="solar:check-circle-bold" width={16} /> CHECK-IN · วันที่ {r.dayNumber}
            </div>
            <div className="ckn-pop-amt">{cash ? `฿${Number(r.rewardAmount).toLocaleString("th-TH")}` : `${Number(r.rewardAmount).toLocaleString("th-TH")} เพชร`}</div>
            <ul>
              {r.turnover_required > 0 && (
                <li>
                  <Icon icon="solar:lock-keyhole-minimalistic-bold" width={16} /> ต้องแทงให้ครบ ฿{Number(r.turnover_required).toLocaleString("th-TH")} ก่อนถอนเงินรางวัลส่วนนี้
                </li>
              )}
              {r.play_label && (
                <li>
                  <Icon icon="solar:ticket-bold" width={16} /> เงินรางวัลนี้แทงได้{r.play_label}
                </li>
              )}
              {result.progress && result.progress.completed && (
                <li className="good">
                  <Icon icon="solar:cup-star-bold" width={16} /> ครบทุกวันแล้ว — ยินดีด้วย!
                </li>
              )}
            </ul>
          </>
        ) : (
          <>
            <Icon icon="fluent-emoji-flat:alarm-clock" width={72} height={72} />
            <div className="ckn-pop-kicker err">เช็คอินไม่สำเร็จ</div>
            <p>{result.message}</p>
          </>
        )}
        <button type="button" ref={closeRef} className="ckn-btn" onClick={onClose}>
          ตกลง
        </button>
      </div>
    </div>
  );
}

export default function CheckinPage({ onDone }) {
  const nav = useNavigate();
  const ck = useCheckin(onDone);
  const { view } = ck;
  const left = useResetClock(view.seconds_to_reset, ck.reload);

  if (!ck.isAuthenticated) {
    return (
      <LoginGate
        icon="solar:calendar-mark-bold-duotone"
        title="เข้าสู่ระบบเพื่อเช็คอินรับรางวัล"
        desc="เช็คอินทุกวันเพื่อเดินตามเส้นทางไปรับรางวัลใหญ่"
        perks={["เช็คอินวันละครั้ง", "สะสมวันรับรางวัลใหญ่", "ดูประวัติการเช็คอิน"]}
        onLogin={() => nav("/auth")}
      />
    );
  }
  if (ck.loading) return <Skeleton h={320} style={{ marginTop: 18 }} />;

  const ev = view.event;
  const btn = buttonState(view, ck.busy);
  const claimed = view.progress ? view.progress.claimed_count : 0;
  const total = ev ? ev.total_days : 0;
  const pct = total ? Math.round((claimed / total) * 100) : 0;

  return (
    <div className="ckn-page" style={ev ? { "--ckn-c": ev.theme_color } : undefined}>
      {!ev ? (
        <div className="lx-card">
          <Empty icon="solar:calendar-mark-bold-duotone">{ck.enabled ? "ยังไม่มีกิจกรรมเช็คอินตอนนี้ — แวะมาใหม่นะ" : "ระบบเช็คอินปิดให้บริการชั่วคราว"}</Empty>
        </div>
      ) : (
        <>
          <section className={`ckn-hero${ck.enabled ? "" : " is-off"}`}>
            <div className="ckn-glow" aria-hidden="true" />
            <div className="ckn-head">
              {ev.icon_url ? <img src={eventImg(ev.icon_url)} alt="" /> : <Icon icon="fluent-emoji-flat:calendar" width={52} height={52} />}
              <div>
                <h1>{ev.title}</h1>
                {ev.subtitle && <p>{ev.subtitle}</p>}
              </div>
              <span className={`ckn-led ${ck.enabled && view.state === "READY" ? "on" : "off"}`}>{view.state === "READY" ? "READY" : view.state === "DONE_TODAY" ? "DONE" : "—"}</span>
            </div>

            <div className="ckn-count" aria-label="ความคืบหน้า">
              <div className="ckn-ring" style={{ "--p": `${pct}%` }}>
                <div>
                  <b>{claimed}</b>
                  <span>/ {total} วัน</span>
                </div>
              </div>
              <ul className="ckn-stats">
                <li>
                  <Icon icon="fluent-emoji-flat:fire" width={22} height={22} />
                  <span>ต่อเนื่อง</span>
                  <b>{view.progress ? view.progress.streakCount : 0} วัน</b>
                </li>
                <li>
                  <Icon icon="fluent-emoji-flat:gem-stone" width={22} height={22} />
                  <span>เพชรสะสม</span>
                  <b>{Number(view.totals.diamonds).toLocaleString("th-TH")}</b>
                </li>
                <li>
                  <Icon icon="fluent-emoji-flat:money-bag" width={22} height={22} />
                  <span>เงินสะสม</span>
                  <b>฿{Number(view.totals.cash).toLocaleString("th-TH")}</b>
                </li>
              </ul>
            </div>

            <button type="button" className="ckn-btn" disabled={btn.disabled} onClick={ck.claim}>
              <Icon icon={ck.busy ? "svg-spinners:ring-resize" : view.state === "READY" ? "solar:hand-stars-bold" : "solar:check-circle-bold"} width={22} />
              {btn.label}
            </button>
            {view.state === "DONE_TODAY" && <p className="ckn-next">รีเซ็ตใน <b>{clockText(left)}</b></p>}
          </section>

          <section className="ckn-sec">
            <h2>
              <Icon icon="solar:map-arrow-right-bold-duotone" width={20} /> เส้นทางรางวัล
            </h2>
            <ol className="ckn-track">
              {view.days.map((d) => {
                const r = rewardInfo(d);
                const last = d.day === total;
                return (
                  <li key={d.day} className={`st-${d.state.toLowerCase()}${last ? " last" : ""}`}>
                    <span className="ckn-node">{d.state === "CLAIMED" ? <Icon icon="solar:check-read-bold" width={20} /> : last ? <Icon icon="solar:cup-star-bold" width={20} /> : d.day}</span>
                    <div className="ckn-day">
                      <small>วันที่ {d.day}</small>
                      <b>
                        <Icon icon={r.icon} width={20} height={20} /> {r.text}
                      </b>
                    </div>
                    {d.state === "TODAY" && <em>วันนี้</em>}
                  </li>
                );
              })}
            </ol>
          </section>
        </>
      )}

      {view.active.length > 0 && (
        <section className="ckn-sec">
          <h2>
            <Icon icon="solar:shield-keyhole-bold-duotone" width={20} /> เงินรางวัลที่ยังมีเงื่อนไข
          </h2>
          <div className="ckn-actives">
            {view.active.map((a) => (
              <article className="ckn-active" key={a.id}>
                <div className="ckn-meter" style={{ "--p": `${a.locked ? turnPct(a) : 100}%` }} aria-hidden="true">
                  <b>{a.locked ? `${Math.round(turnPct(a))}%` : <Icon icon="solar:ticket-bold" width={18} />}</b>
                </div>
                <div className="ckn-active-body">
                  <div>
                    <code>เช็คอินวันที่ {a.day_number}</code>
                    <strong>฿{Number(a.amount).toLocaleString("th-TH")}</strong>
                  </div>
                  {a.locked && (
                    <span>
                      ทำเทิร์นแล้ว ฿{Number(a.turnover_done).toLocaleString("th-TH")} จาก ฿{Number(a.turnover_required).toLocaleString("th-TH")}
                    </span>
                  )}
                  {a.restricted_left > 0 && (
                    <span>
                      เหลือ ฿{Number(a.restricted_left).toLocaleString("th-TH")} · {a.play_label}
                    </span>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      <section className="ckn-sec">
        <h2>
          <Icon icon="solar:history-bold-duotone" width={20} /> ประวัติการเช็คอิน {view.totals.days > 0 && <small>{view.totals.days} ครั้ง</small>}
        </h2>
        {view.history.length === 0 ? (
          <div className="lx-card">
            <Empty icon="solar:calendar-mark-bold-duotone">ยังไม่เคยเช็คอิน</Empty>
          </div>
        ) : (
          <ol className="ckn-timeline">
            {view.history.map((h) => {
              const r = rewardInfo({ reward_type: h.reward_type, amount: h.amount });
              return (
                <li key={h.id}>
                  <span className="ckn-dot">
                    <Icon icon="solar:check-read-bold" width={14} />
                  </span>
                  <div className="ckn-tl-body">
                    <b>
                      {h.event_title || "เช็คอิน"} · วันที่ {h.day_number}
                    </b>
                    <span>{whenText(h.claimed_at)}</span>
                  </div>
                  <strong className={r.cash ? "cash" : "gem"}>+{r.text}</strong>
                </li>
              );
            })}
          </ol>
        )}
      </section>

      {view.rules.length > 0 && (
        <section className="ckn-sec">
          <h2>
            <Icon icon="solar:document-text-bold-duotone" width={20} /> กติกา
          </h2>
          <ul className="ckn-rules">
            {view.rules.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        </section>
      )}

      <Reward result={ck.result} onClose={ck.closeResult} />
    </div>
  );
}
